<template>
  <div class="w-full bg-slate-50 min-h-screen p-4 md:p-8" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-2xl font-black text-slate-800 tracking-tight">
            {{ $route.name }}
          </h1>
          <p class="text-sm text-slate-500 font-medium mt-1">
            Gerencie e acompanhe os abastecimentos da frota
          </p>
        </div>
      </div>

      <!-- DATA TABLE CARD -->
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-gas-station</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              REGISTROS DE ABASTECIMENTO
            </h2>
          </div>

          <div class="flex items-center gap-4 flex-1 justify-end">
            <v-tooltip bottom color="indigo">
              <template v-slot:activator="{ on, attrs }">
                <button 
                  v-bind="attrs"
                  v-on="on"
                  @click="openExtraModal" 
                  class="bg-indigo-500 hover:bg-indigo-600 mr-2 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-4 py-2 transition-all w-fit whitespace-nowrap"
                  style="width: auto !important; flex: none !important;"
                >
                  <v-icon color="white" x-small>mdi-gas-station</v-icon>
                  <span class="font-bold text-[10px] uppercase tracking-wider">ABASTECIMENTO EXTRA</span>
                </button>
              </template>
              <span>Autorização p/ Garrafas/Equipamentos</span>
            </v-tooltip>

            <v-tooltip bottom color="orange">
              <template v-slot:activator="{ on, attrs }">
                <button 
                  v-bind="attrs"
                  v-on="on"
                  @click="$router.push('/supplies-manager/create')" 
                  class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-4 py-2 transition-all w-fit whitespace-nowrap"
                  style="width: auto !important; flex: none !important;"
                >
                  <v-icon color="white" x-small>mdi-plus</v-icon>
                  <span class="font-bold text-[10px] uppercase tracking-wider">NOVO ABASTECIMENTO</span>
                </button>
              </template>
              <span>Criar nova autorização</span>
            </v-tooltip>
            
            <div class="relative w-72">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
              </div>
              <v-tooltip bottom color="indigo">
                <template v-slot:activator="{ on, attrs }">
                  <input 
                    v-bind="attrs"
                    v-on="on"
                    v-model="search"
                    @keyup.enter="initialize"
                    placeholder="PESQUISAR (+ ENTER)"
                    class="bg-white/10 border border-white/20 rounded-lg py-1.5 pl-10 pr-4 text-[10px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest font-bold"
                  >
                </template>
                <span>Digite a placa, cupom ou setor e aperte ENTER para buscar no histórico geral</span>
              </v-tooltip>
            </div>

            <v-btn
              icon
              small
              dark
              @click="initialize"
              class="bg-white/10 hover:bg-white/20 transition-colors"
            >
              <v-icon small>mdi-update</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="p-0">
          <v-data-table
            :headers="headers"
            :items="data"
            :search="search"
            :items-per-page="10"
            :loading="loader"
            loading-text="CARREGANDO..."
            class="elevation-0"
          >
            <template v-slot:item.supply_code="{ item }">
              <span class="font-mono text-[11px] font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded">
                {{ item.supply_code ? item.supply_code : item.vehicle_password }}
              </span>
            </template>

            <template v-slot:item.model_desc="{ item }">
              <div class="flex flex-col py-2">
                <span class="text-sm font-bold text-slate-800">{{ item.vehicle_board }}</span>
                <span class="text-[10px] text-slate-500 font-medium uppercase">{{ item.brand_desc }} - {{ item.model_desc }}</span>
              </div>
            </template>
            
            <template v-slot:item.datehour="{ item }">
              <span class="text-sm text-slate-600 font-medium">{{ convertDateTime(item.datehour) }}</span>
            </template>

            <template v-slot:item.supply_ajusted="{ item }">
              <v-chip
                x-small
                :color="item.supply_ajusted ? 'success' : 'slate-200'"
                label
                class="font-bold text-[9px]"
                :dark="item.supply_ajusted"
              >
                {{ item.supply_ajusted ? "SIM" : "NÃO" }}
              </v-chip>
            </template>

            <template v-slot:item.supply_status="{ item }">
              <v-chip
                x-small
                :color="item.supply_status === 'CONCLUIDO' ? 'success' : 'orange'"
                label
                dark
                class="font-bold text-[9px]"
              >
                {{ item.supply_status }}
              </v-chip>
            </template>

            <template v-slot:item.supply_billed="{ item }">
              <v-chip
                x-small
                :color="item.supply_billed ? 'indigo' : 'slate-300'"
                label
                class="font-bold text-[9px]"
                :dark="item.supply_billed"
              >
                {{ item.supply_billed ? "FATURADO" : "PENDENTE" }}
              </v-chip>
            </template>

            <template v-slot:item.supply_liters_stocked="{ item }">
              <span class="text-sm font-bold text-emerald-600">
                {{ item.supply_liters_stocked ? realMaskII(item.supply_liters_stocked.toFixed(3)) : '0,000' }}
              </span>
            </template>

            <template v-slot:item.action="{ item }">
              <div class="flex items-center justify-end gap-1">
                <v-tooltip bottom color="purple">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="[(showPassword = true), (password = item.supply_code)]">
                      <v-icon small color="purple">mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver Senha</span>
                </v-tooltip>

                <v-tooltip bottom color="primary" v-if="item.supply_status != 'CONCLUIDO'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="$router.push(`/supplies-manager/edit/${item.supply_id}`)">
                      <v-icon small color="primary">mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>

                <v-tooltip bottom color="success">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="openModal(item.supply_id)">
                      <v-icon small color="success">mdi-square-edit-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Corrigir</span>
                </v-tooltip>

                <v-tooltip bottom color="error">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="deleteItem(item.user_id)">
                      <v-icon small color="error">mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Excluir</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>
    </div>
    <v-dialog v-model="dialog" width="600" persistent>
      <v-card class="rounded-xl overflow-hidden shadow-2xl">
        <!-- Header Escuro -->
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-pencil-box-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-xs">Alterar Abastecimento</h2>
          </div>
          <v-btn icon dark small @click="dialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="p-8">
          <v-form ref="form" v-model="valid">
            <div class="space-y-6">
              <!-- Data e Hora -->
              <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Data e Hora do Abastecimento</label>
                <v-text-field
                  v-mask="'##/##/#### ##:##'"
                  v-model="dataAdjust.datehour"
                  outlined dense hide-details
                  class="bg-white rounded-lg shadow-sm"
                  prepend-inner-icon="mdi-calendar-clock"
                />
              </div>

              <!-- Veículo e Item -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Veículo</label>
                  <v-autocomplete
                    :items="vehicles"
                    item-value="vehicle_id"
                    item-text="vehicle_board"
                    v-model="dataAdjust.vehicle_id"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      <span class="text-xs font-bold" v-if="item">{{ item.vehicle_board }}</span>
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      <div class="flex flex-col py-1" v-if="item">
                        <span class="text-xs font-bold">{{ item.vehicle_board }}</span>
                        <span class="text-[9px] uppercase text-slate-500">{{ item.brand_desc }} - {{ item.model_desc }}</span>
                      </div>
                    </template>
                  </v-autocomplete>
                </div>

                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Item/Combustível</label>
                  <v-autocomplete
                    :items="items"
                    item-value="item_id"
                    item-text="item_description_desc"
                    v-model="dataAdjust.item_id"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                  />
                </div>
              </div>

              <!-- Valores -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Litros</label>
                  <v-text-field
                    v-model="dataAdjust.supply_liters_stocked"
                    v-mask-decimal.br="3"
                    @change="calculateSubTotals"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm font-bold text-center"
                  />
                </div>
                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Preço Dia</label>
                  <v-text-field
                    v-model="dataAdjust.supply_price_day"
                    v-mask-decimal.br="3"
                    @change="calculateSubTotals"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm font-bold text-center"
                  />
                </div>
                <div class="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                  <label class="block text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Valor Total</label>
                  <v-text-field
                    readonly
                    v-model="dataAdjust.supply_value"
                    v-mask-decimal.br="2"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm font-black text-center text-indigo-700"
                    prefix="R$"
                  />
                </div>
              </div>

              <!-- Cupom e Justificativa -->
              <div class="space-y-4">
                <div class="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Número do Cupom</label>
                  <v-text-field
                    v-model="dataAdjust.supply_coupon"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                  />
                </div>

                <div v-if="dataAdjust.supply_extra" class="bg-amber-50 p-4 rounded-xl border border-amber-100">
                  <label class="block text-[10px] font-black text-amber-500 uppercase tracking-widest mb-2">Justificativa</label>
                  <v-textarea
                    v-model="dataAdjust.justificativa"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm"
                    rows="2"
                    auto-grow
                  />
                </div>
              </div>
            </div>
          </v-form>
        </v-card-text>

        <div class="px-8 py-6 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
          <v-btn
            depressed
            large
            color="secondary"
            @click="dialog = false"
            class="rounded-xl font-bold px-8"
            text
          >
            Cancelar
          </v-btn>
          <v-btn
            depressed
            large
            color="orange"
            :loading="loader"
            @click="ajust"
            class="rounded-xl font-black px-12 shadow-lg white--text"
          >
            Confirmar Alteração
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showPassword" width="300">
      <v-card>
        <v-card-title class="headline primary lighten-2 text-center"
          >COPIE SUA SENHA</v-card-title
        >
        <v-card-text class="mt-6 text-center">
          <v-row>
            <v-col cols="12" sm="12">
              <v-label>
                <input
                  readonly
                  class="text-h4 text-center"
                  style="width: 100%; border: 0; outline: none"
                  ref="copiar"
                  :value="password"
                />
              </v-label>
            </v-col>
            <v-col cols="12" sm="12">
              <v-btn
                :color="copiado ? 'green' : 'primary'"
                depressed
                :dark="copiado"
                block
                @click="copy"
                :outlined="!copiado"
              >
                <v-icon v-if="copiado">mdi-check</v-icon>
                {{ copiado ? "COPIADO" : "COPIAR" }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar
      multi-line
      :color="snack.color"
      v-model="snack.show"
      transition="fade-transition"
    >
      {{ snack.message }}
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    
    <!-- MODAL ABASTECIMENTO EXTRA -->
    <v-dialog v-model="dialogExtra" width="500" persistent>
      <v-card class="rounded-xl overflow-hidden shadow-2xl">
        <div class="px-6 py-4 flex items-center justify-between" style="background-color: #3f51b5;">
          <div class="flex items-center gap-3">
            <v-icon color="white" small>mdi-gas-station</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-xs">Abastecimento Extra (02)</h2>
          </div>
          <v-btn icon dark small @click="dialogExtra = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="p-6">
          <v-form ref="formExtra" v-model="validExtra">
            <div class="space-y-4">
              <div class="bg-indigo-50 p-3 rounded-lg border border-indigo-100 mb-2">
                <p class="text-xs text-indigo-700 font-medium">Este abastecimento não está associado a um veículo e ficará retido ({{"BLOQUEADO PARA DISTRIBUIÇÃO"}}).</p>
              </div>

              <!-- Fornecedor e Item -->
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Fornecedor</label>
                <v-autocomplete
                  :items="providers"
                  item-value="provider_id"
                  item-text="provider_name"
                  v-model="dataExtra.provider_id"
                  @change="loadItemsByProvider"
                  outlined dense hide-details
                  class="bg-white rounded-lg shadow-sm"
                />
              </div>

              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Item / Combustível</label>
                <v-autocomplete
                  :items="itemsExtra"
                  item-value="item_id"
                  item-text="item_description_desc"
                  v-model="dataExtra.item_id"
                  :disabled="itemsExtra.length === 0"
                  outlined dense hide-details
                  class="bg-white rounded-lg shadow-sm"
                />
              </div>

              <!-- Valores Autorizados -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Litros Autorizados</label>
                  <v-text-field
                    v-model="dataExtra.supply_authorized_liters"
                    v-mask-decimal.br="3"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm font-bold text-center"
                  />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Valor Autorizado (R$)</label>
                  <v-text-field
                    v-model="dataExtra.supply_authorized_value"
                    v-mask-decimal.br="2"
                    outlined dense hide-details
                    class="bg-white rounded-lg shadow-sm font-bold text-center text-indigo-700"
                  />
                </div>
              </div>

              <div>
                <label class="block text-[10px] font-black text-amber-500 uppercase tracking-widest mb-1">Justificativa Opcional</label>
                <v-textarea
                  v-model="dataExtra.justificativa"
                  outlined dense hide-details
                  class="bg-white rounded-lg shadow-sm"
                  rows="2" auto-grow
                />
              </div>
            </div>
          </v-form>
        </v-card-text>

        <div class="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
          <v-btn depressed color="secondary" @click="dialogExtra = false" class="rounded-lg font-bold px-6" text>Cancelar</v-btn>
          <v-btn depressed color="indigo" :loading="loader" @click="saveExtra" class="rounded-lg font-black px-8 shadow-md white--text">Salvar</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
export default {
  mixins: [Helpers],
  data() {
    return {
      search: "",
      loader: true,
      copiado: false,
      password: "",
      showPassword: false,
      dialog: false,
      dialogExtra: false,
      validExtra: true,
      dataExtra: {
        provider_id: null,
        item_id: null,
        supply_authorized_liters: 0,
        supply_authorized_value: 0,
        justificativa: null,
      },
      itemsExtra: [],
      data: [],
      providers: [],
      items: [],
      vehicles: [],
      fieldRules: [(v) => !!v || "Este campo é requerido"],
      prefecture_id: null,
      snack: {
        show: false,
        message: "",
        color: "",
      },
      dataAdjust: {
        supply_id: null,
        vehicle_id: null,
        provider_id: null,
        item_id: null,
        supply_liters_stocked: 0,
        supply_price_day: 0,
        supply_coupon: null,
        supply_value: 0,
        supply_extra: false,
        justificativa: null,
      },
      headers: [
        {
          text: "Data/Hora",
          value: "datehour",
          sortable: false,
          align: "left",
        },
        { text: "Setor", value: "sec_name", sortable: false, align: "left" },
        { text: "Veículo", value: "model_desc", sortable: false },
        {
          text: "Item",
          value: "item_description_desc",
          sortable: false,
          align: "left",
        },
        {
          text: "Total de Litros",
          value: "supply_liters_stocked",
          sortable: false,
          align: "left",
        },
        {
          text: "Cupom",
          value: "supply_coupon",
          sortable: false,
          align: "left",
        },
        {
          text: "Status",
          value: "supply_status",
          sortable: false,
          align: "left",
        },
        {
          text: "Corrigido",
          value: "supply_ajusted",
          sortable: false,
          align: "left",
        },
        {
          text: "Faturado",
          value: "supply_billed",
          sortable: false,
          align: "left",
        },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
    };
  },
  created() {
    this.Helpers;
    this.prefecture_id = this.$store.getters.getUserData.table_id;
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      
      let url = `/supplies/${this.prefecture_id}`;
      // Se houver um termo de pesquisa, enviamos para a API. A API se encarrega de ignorar o filtro de dados do dia.
      if (this.search && this.search.trim().length > 0) {
        url += `?search=${encodeURIComponent(this.search.trim())}`;
      }

      this.$http
        .get(url)
        .then((r) => {
          this.data = r.data;
          this.loader = false;
          this.snack.color = "success";
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.type = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    copy() {
      var copyTextarea = this.$refs.copiar;
      copyTextarea.select();
      try {
        var successful = document.execCommand("copy");
        if (successful) {
          this.copiado = true;
          setTimeout(() => {
            this.showPassword = false;
            this.copiado = false;
          }, 1000);
        } else {
          alert("não copiado!");
        }
      } catch (err) {
        alert(
          "Opa, Não conseguimos copiar o texto, é possivel que o seu navegador não tenha suporte, tente usar Crtl+C."
        );
      }
    },
    ajust() {
      delete this.dataAdjust.supply_authorized_liters;
      delete this.dataAdjust.supply_authorized_value;
      this.dataAdjust.supply_adjusted = true;
      this.$http
        .put(`/supplies/single/${this.dataAdjust.supply_id}`, this.dataAdjust)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.loader = true;
          setTimeout(() => {
            this.dialog = false;
            this.initialize();
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    openModal(id) {
      this.$http.get(`/supplies/single/${id}`).then((r) => {
        const supplyData = r.data[0];
        
        // Carrega veículos da prefeitura ESPECÍFICA deste abastecimento
        // Isso resolve o problema para usuários MASTER (csifrota)
        this.$http.get(`/vehicles/supplies/${supplyData.prefecture_id}`).then((v) => {
          this.vehicles = v.data;
          
          // Carrega Itens/Combustíveis usando os IDs da própria supply
          this.$http.get(`/supplies/providers/prefectures/${supplyData.provider_id}/${supplyData.prefecture_id}`).then((i) => {
            this.items = i.data;
            
            // Popula dataAdjust APÓS as listas estarem prontas e com match garantido
            this.dataAdjust = Object.assign({}, supplyData);
            this.dataAdjust.supply_liters_stocked = supplyData.supply_liters_stocked.toFixed(3);
            this.dataAdjust.supply_price_day = supplyData.supply_price_day.toFixed(3);
            this.dataAdjust.datehour = this.convertDateTime(supplyData.datehour);
            
            this.dialog = true;
          });
        });
      });
    },
    calculateSubTotals() {
      if (
        this.dataAdjust.supply_liters_stocked &&
        this.dataAdjust.supply_price_day
      ) {
        let subtotal =
          this.convertDecimalNumberII(this.dataAdjust.supply_price_day) *
          this.convertDecimalNumberII(this.dataAdjust.supply_liters_stocked);
        this.dataAdjust.supply_value = subtotal.toFixed(2);
      }
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/supplies/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    openExtraModal() {
      // Carregar os fornecedores normais se ainda não tiverem sido carregados
      if (this.providers.length === 0) {
        this.$http.get(`/providers/prefecture/${this.prefecture_id}`).then(r => {
          this.providers = r.data;
        });
      }
      this.dataExtra = {
        provider_id: null,
        item_id: null,
        supply_authorized_liters: 0,
        supply_authorized_value: 0,
        justificativa: null,
      };
      this.itemsExtra = [];
      this.dialogExtra = true;
    },
    loadItemsByProvider() {
      if (this.dataExtra.provider_id) {
        this.itemsExtra = [];
        this.dataExtra.item_id = null;
        this.$http.get(`/supplies/providers/prefectures/${this.dataExtra.provider_id}/${this.prefecture_id}`).then(i => {
          this.itemsExtra = i.data;
        });
      }
    },
    saveExtra() {
      if (!this.dataExtra.provider_id || !this.dataExtra.item_id || !this.dataExtra.supply_authorized_liters) {
        this.snack.message = "Preencha Fornecedor, Combustível e os Litros Autorizados.";
        this.snack.color = "warning";
        this.snack.show = true;
        return;
      }

      this.loader = true;
      const payload = {
        ...this.dataExtra,
        prefecture_id: this.prefecture_id,
        is_autorized: true,
        authorizer: this.$store.getters.getUserData.user_login,
      };

      this.$http
        .post("/supplies/extra", payload)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = "Abastecimento Extra registrado com sucesso! (BLOQUEADO)";
          this.snack.color = "success";
          this.dialogExtra = false;
          
          if (r.data && r.data.code) {
            this.password = r.data.code;
            this.showPassword = true;
          }

          // Não listaremos extras da tela normal, logo, recarregar initialize() 
          // não fará este registro aparecer, mas pode atualizar outros registros.
          this.initialize();
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message || "Erro ao salvar Abastecimento Extra";
          this.snack.color = "error";
          this.loader = false;
        });
    },
  },
};
</script>

<style scoped>
.top-bar {
  padding: 30px 15px;
}
.title {
  text-transform: uppercase;
  font-weight: 400;
}
.maxPage {
  width: 100%;
}
.search-field {
  margin-top: 10px;
}
.space {
  margin: 0 10px;
}
</style>
